import React, { useEffect, useState } from 'react'
import LayoutHeader from '../../layout/LayoutHeader'
import { useNavigate } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { setErrorAlert, setSuccessAlert, setLoader } from '../../store/alert/alertSlice'
import { postDataAPI, getDataAPI } from '../../utils/fetchData'

const EmailTracking = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { user } = useSelector((state) => state.auth)
    
    const [loading, setLoading] = useState(true)
    const [trackedEmails, setTrackedEmails] = useState([])
    const [newEmail, setNewEmail] = useState('')
    const [isAdding, setIsAdding] = useState(false)
    const [isSyncing, setIsSyncing] = useState(false)

    useEffect(() => {
        fetchTrackedEmails()
    }, [])

    const fetchTrackedEmails = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            const response = await getDataAPI('user/gmail/tracked-emails', token)
            
            if (response.data.status === 200) {
                setTrackedEmails(response.data.data.trackedEmails || [])
            }
        } catch (error) {
            console.error('Error fetching tracked emails:', error)
            // If Gmail isn't connected, show empty state
            setTrackedEmails([])
        } finally {
            setLoading(false)
        }
    }

    const handleAddEmail = async () => {
        if (!newEmail.trim()) {
            dispatch(setErrorAlert('Please enter an email address'))
            return
        }
        
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(newEmail.trim())) {
            dispatch(setErrorAlert('Please enter a valid email address'))
            return
        }
        
        // Check if email is already tracked
        if (trackedEmails.some(email => email.email.toLowerCase() === newEmail.trim().toLowerCase())) {
            dispatch(setErrorAlert('This email is already being tracked'))
            return
        }
        
        try {
            setIsAdding(true)
            dispatch(setLoader(true))
            
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            const response = await postDataAPI('user/gmail/add-tracked-email', {
                email: newEmail.trim(),
                label: 'Industry Contact',
                priority: 'medium'
            }, token)
            
            if (response.data.status === 200) {
                setTrackedEmails([...trackedEmails, response.data.data.trackedEmail])
                setNewEmail('')
                dispatch(setSuccessAlert('Email contact added for tracking'))
            } else {
                dispatch(setErrorAlert('Failed to add email for tracking'))
            }
        } catch (error) {
            console.error('Error adding tracked email:', error)
            dispatch(setErrorAlert('Error adding email for tracking'))
        } finally {
            setIsAdding(false)
            dispatch(setLoader(false))
        }
    }

    const handleRemoveEmail = async (emailId) => {
        try {
            dispatch(setLoader(true))
            
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            const response = await postDataAPI('user/gmail/remove-tracked-email', {
                emailId
            }, token)
            
            if (response.data.status === 200) {
                setTrackedEmails(trackedEmails.filter(email => email.id !== emailId))
                dispatch(setSuccessAlert('Email contact removed from tracking'))
            } else {
                dispatch(setErrorAlert('Failed to remove email from tracking'))
            }
        } catch (error) {
            console.error('Error removing tracked email:', error)
            dispatch(setErrorAlert('Error removing email from tracking'))
        } finally {
            dispatch(setLoader(false))
        }
    }

    const handleSyncEmails = async () => {
        try {
            setIsSyncing(true)
            dispatch(setLoader(true))
            
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            const response = await postDataAPI('user/gmail/trigger-sync', {}, token)
            
            if (response.data.status === 200) {
                dispatch(setSuccessAlert('Sync Complete'))
                
                // Refresh the tracked emails list to show updated counts
                fetchTrackedEmails()
            } else {
                dispatch(setErrorAlert('Failed to sync emails'))
            }
        } catch (error) {
            console.error('Error syncing emails:', error)
            dispatch(setErrorAlert('Error syncing emails. Make sure Gmail is connected.'))
        } finally {
            setIsSyncing(false)
            dispatch(setLoader(false))
        }
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAddEmail()
        }
    }

    if (loading) {
        return (
            <LayoutHeader>
                <div className="w-[390px] flex flex-col justify-center items-center h-screen">
                    <div className="text-white">Loading email tracking...</div>
                </div>
            </LayoutHeader>
        )
    }

    return (
        <LayoutHeader>
            <div className="w-[390px] flex flex-col justify-start items-center relative z-[0]">
                <div className="px-4 my-[50px] flex w-full justify-between">
                    <p className="text-white">Email</p>
                    <div className='flex gap-[20px]'>
                        <p 
                            onClick={handleSyncEmails} 
                            className={`cursor-pointer ${isSyncing ? 'text-gray-400' : 'text-white'}`}
                        >
                            {isSyncing ? 'Syncing...' : 'Sync'}
                        </p>
                        <p onClick={() => navigate('/email')} className='text-white cursor-pointer'>Go back</p>
                    </div>
                </div>

                <div className="w-full px-4 space-y-4 pb-8">
                    {/* Add New Email Contact */}
                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                        <h3 className="text-white text-[14px] font-semibold mb-3">Add Email Contact to Track</h3>
                        <div className="space-y-3">
                            <input
                                type="email"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Email address (e.g., bookLabel@venue.com)"
                                className="w-full bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded-md px-3 py-3 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[rgba(255,255,255,0.4)]"
                            />
                            <button
                                onClick={handleAddEmail}
                                disabled={isAdding || !newEmail.trim()}
                                className="w-full bg-[rgba(100,100,100,0.8)] text-white text-[14px] font-medium py-3 rounded-md hover:bg-[rgba(120,120,120,0.8)] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                {isAdding ? 'Adding Email Contact...' : 'Add Email Contact'}
                            </button>
                        </div>
                    </div>

                    {/* Tracked Email Contacts */}
                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-white text-[14px] font-semibold">Tracked Email Contacts ({trackedEmails.length})</h3>
                        </div>
                        <p className="text-[rgba(255,255,255,0.6)] text-[12px] mb-4">
                            These contacts will be prioritized in AI analysis and insights
                        </p>
                        
                        {trackedEmails.length > 0 ? (
                            <div className="space-y-2">
                                {trackedEmails.map((email) => (
                                    <div key={email.id} className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-md p-3">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-white text-[12px] font-medium">{email.email}</p>
                                                <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                                    Added {new Date(email.createdAt).toLocaleDateString()}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => handleRemoveEmail(email.id)}
                                                className="text-[#EF4444] text-[10px] hover:opacity-70 px-2 py-1"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-6">
                                <p className="text-[rgba(255,255,255,0.6)] text-[14px] mb-2">No email contacts tracked yet</p>
                                <p className="text-[rgba(255,255,255,0.4)] text-[12px]">
                                    Add important contacts like venue bookers, record labels, or managers
                                </p>
                            </div>
                        )}
                    </div>

                    {/* How Email Tracking Works */}
                    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                        <h3 className="text-white text-[14px] font-semibold mb-3">How Email Tracking Works</h3>
                        <div className="space-y-3 text-[12px]">
                            <div className="flex items-start gap-2">
                                <span className="text-white font-medium">• High Priority:</span>
                                <span className="text-[rgba(255,255,255,0.7)]">Critical contacts like major venue bookers or record labels</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-white font-medium">• Medium Priority:</span>
                                <span className="text-[rgba(255,255,255,0.7)]">Important industry contacts and regular collaborators</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-white font-medium">• Low Priority:</span>
                                <span className="text-[rgba(255,255,255,0.7)]">General industry contacts and occasional communications</span>
                            </div>
                            <p className="text-[rgba(255,255,255,0.6)] text-[11px] mt-3 pt-2 border-t border-[rgba(255,255,255,0.1)]">
                                Tracked contacts help Dizee's AI provide better context and insights about your music industry communications.
                            </p>
                        </div>
                    </div>
                </div>


            </div>
        </LayoutHeader>
    )
}

export default EmailTracking 